import { Box, CircularProgress } from "@mui/material";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "../../../../../../contexts/ChosenDepartment";
import { useDebouncedSave } from "../../../../../../hooks/useDebouncedSave";
import { useErrorSnackbar } from "../../../../../../hooks/useErrorSnackbar";
import { useDepartmentTimeOffTypeMapping, useHrisPtoTypeStats, useHrisRawPtoEventTypes, useUpsertTimeOffMapping } from "../hrisMappingHooks";
import { PtoTypeChangeConfirmDialog } from "../PtoTypeChangeConfirmDialog";
import { EXCLUDED_VALUE, HrisRawPtoTypes } from "./HrisRawPtoTypes";
import { HrisTimeOffOverview } from "./HrisTimeOffOverview";
import { type PtoTypeRow, usePtoTypeRows } from "./usePtoTypeRows";

/**
 * Pretty Mapping view of the Time Off Types tab (DAT-200) — the Time Off analog of `HrisJobAlignment`.
 *
 * Layout: a 2-column flex row.
 * - Left pane: raw PTO event types with a per-type PTO Type picker + last-60-day count, expandable to the time-offs.
 * - Right pane: read-only summary of the resolved raw-type -> PTO type mapping.
 *
 * Changing a type to a real mapping while it has time-offs in the last 60 days raises a confirmation that
 * lets the admin also re-map that history; otherwise the change applies going forward only.
 */
export const HrisTimeOffAlignment: FC = () => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const departmentId = chosenDepartment.department_id;
	const { data: mappings, isLoading: isLoadingMapping } = useDepartmentTimeOffTypeMapping(departmentId);
	const { data: rawPtoTypes, isLoading: isLoadingRaw } = useHrisRawPtoEventTypes(departmentId);
	const { data: stats } = useHrisPtoTypeStats(departmentId);
	const upsertMutation = useUpsertTimeOffMapping(departmentId);
	const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());
	// Pending PTO type change awaiting confirmation when the raw type has time-offs in the last 60 days.
	const [ptoTypeChangeTarget, setPtoTypeChangeTarget] = useState<{ row: PtoTypeRow; ptoType: string; count: number } | null>(null);

	const rows = usePtoTypeRows(mappings, rawPtoTypes, stats);
	const [search, setSearch] = useState("");

	const filteredRows = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return rows;
		return rows.filter((r) => r.rawString.toLowerCase().includes(q) || r.timeOffs.some((to) => to.employeeName.toLowerCase().includes(q)));
	}, [rows, search]);

	const scheduleSave = useDebouncedSave<{ rawString: string; ptoType: string | null; reason?: string | null; remapPrevious?: boolean }>((payload) => {
		upsertMutation.mutate(payload, {
			onError: (err) => showErrorSnackbar(t("hris.mapping.saveError"), {}, err),
			onSettled: () =>
				setPendingKeys((prev) => {
					const next = new Set(prev);
					next.delete(payload.rawString);
					return next;
				}),
		});
	});

	const saveTimeOff = useCallback(
		(row: PtoTypeRow, ptoType: string | null, remapPrevious: boolean) => {
			setPendingKeys((prev) => new Set(prev).add(row.rawString));
			scheduleSave(row.rawString, {
				rawString: row.rawString,
				ptoType,
				reason: ptoType === null ? null : (row.reason ?? null),
				...(remapPrevious ? { remapPrevious } : {}),
			});
		},
		[scheduleSave],
	);

	// Choosing a real type while time-offs exist in the last 60 days confirms first (so the admin can also
	// re-map history); "Do not sync" or no history saves going forward — there's nothing to re-map.
	const handlePtoTypeChange = useCallback(
		(rawString: string, value?: string) => {
			if (value === undefined) return;
			const row = rows.find((r) => r.rawString === rawString);
			if (!row) return;
			const ptoType = value === EXCLUDED_VALUE ? null : value;
			if (ptoType !== null && row.count > 0) {
				setPtoTypeChangeTarget({ row, ptoType, count: row.count });
				return;
			}
			saveTimeOff(row, ptoType, false);
		},
		[rows, saveTimeOff],
	);

	const handleConfirmPtoTypeChange = useCallback(
		(remapPrevious: boolean) => {
			if (!ptoTypeChangeTarget) return;
			saveTimeOff(ptoTypeChangeTarget.row, ptoTypeChangeTarget.ptoType, remapPrevious);
			setPtoTypeChangeTarget(null);
		},
		[ptoTypeChangeTarget, saveTimeOff],
	);

	if (isLoadingMapping || isLoadingRaw) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "row", gap: 2, height: "100%", overflow: "hidden" }} data-testid="hris-mapping-time-off-alignment">
			<Box sx={{ flex: "0 0 520px", minWidth: 0, height: "100%" }}>
				<HrisRawPtoTypes rows={filteredRows} pendingKeys={pendingKeys} onPtoTypeChange={handlePtoTypeChange} search={search} setSearch={setSearch} />
			</Box>
			<Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
				<HrisTimeOffOverview rows={filteredRows} pendingKeys={pendingKeys} />
			</Box>
			{ptoTypeChangeTarget && (
				<PtoTypeChangeConfirmDialog
					key={ptoTypeChangeTarget.row.rawString}
					open
					rawString={ptoTypeChangeTarget.row.rawString}
					currentPtoTypeLabel={ptoTypeChangeTarget.row.ptoType ?? t("hris.mapping.excluded")}
					ptoTypeLabel={ptoTypeChangeTarget.ptoType}
					countLast60={ptoTypeChangeTarget.count}
					onConfirm={handleConfirmPtoTypeChange}
					onClose={() => setPtoTypeChangeTarget(null)}
				/>
			)}
		</Box>
	);
};
